import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
export const ASSET_NAME = 'landing-page';
// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/LandingPage/SET_INITIAL_STATE';

export const QUERY_BEST_MATCH_LISTINGS_REQUEST =
  'app/LandingPage/QUERY_BEST_MATCH_LISTINGS_REQUEST';
export const QUERY_BEST_MATCH_LISTINGS_SUCCESS =
  'app/LandingPage/QUERY_BEST_MATCH_LISTINGS_SUCCESS';
export const QUERY_BEST_MATCH_LISTINGS_ERROR = 'app/LandingPage/QUERY_BEST_MATCH_LISTINGS_ERROR';

export const QUERY_UPCOMING_SESSIONS_REQUEST = 'app/LandingPage/QUERY_UPCOMING_SESSIONS_REQUEST';
export const QUERY_UPCOMING_SESSIONS_SUCCESS = 'app/LandingPage/QUERY_UPCOMING_SESSIONS_SUCCESS';
export const QUERY_UPCOMING_SESSIONS_ERROR = 'app/LandingPage/QUERY_UPCOMING_SESSIONS_ERROR';
// ================ Reducer ================ //

const initialState = {
  bestMatchListings: null,
  queryBestMatchListingsError: null,
  queryBestMatchListingsInProgress: false,

  upcomingSessions: null,
  queryUpcomingSessionsError: null,
  queryUpcomingSessionsInProgress: false,
};

export default function landingPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };

    case QUERY_BEST_MATCH_LISTINGS_REQUEST:
      return {
        ...state,
        bestMatchListings: null,
        queryBestMatchListingsError: null,
        queryBestMatchListingsInProgress: true,
      };
    case QUERY_BEST_MATCH_LISTINGS_SUCCESS:
      return {
        ...state,
        bestMatchListings: payload.bestMatchListings,
        queryBestMatchListingsInProgress: false,
      };
    case QUERY_BEST_MATCH_LISTINGS_ERROR:
      return {
        ...state,
        bestMatchListings: null,
        queryBestMatchListingsError: payload,
        queryBestMatchListingsInProgress: false,
      };

    case QUERY_UPCOMING_SESSIONS_REQUEST:
      return {
        ...state,
        upcomingSessions: null,
        queryUpcomingSessionsError: null,
        queryUpcomingSessionsInProgress: true,
      };
    case QUERY_UPCOMING_SESSIONS_SUCCESS:
      return {
        ...state,
        upcomingSessions: payload.upcomingSessions,
        queryUpcomingSessionsInProgress: false,
      };
    case QUERY_UPCOMING_SESSIONS_ERROR:
      return {
        ...state,
        upcomingSessions: null,
        queryUpcomingSessionsError: payload,
        queryUpcomingSessionsInProgress: false,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const queryBestMatchListingsRequest = userId => ({
  type: QUERY_BEST_MATCH_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryBestMatchListingsSuccess = bestMatchListings => ({
  type: QUERY_BEST_MATCH_LISTINGS_SUCCESS,
  payload: { bestMatchListings },
});

export const queryBestMatchListingsError = e => ({
  type: QUERY_BEST_MATCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryUpcomingSessionsRequest = _userId => ({
  type: QUERY_UPCOMING_SESSIONS_REQUEST,
  payload: {},
});

export const queryUpcomingSessionsSuccess = upcomingSessions => ({
  type: QUERY_UPCOMING_SESSIONS_SUCCESS,
  payload: { upcomingSessions },
});

export const queryUpcomingSessionsError = e => ({
  type: QUERY_UPCOMING_SESSIONS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const queryBestMatchListings = config => (dispatch, getState, sdk) => {
  dispatch(queryBestMatchListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      page: 1,
      perPage: 6,
      include: ['author', 'images'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));

      const imagesArray = response.data.included.filter(i => {
        return i.type === 'image';
      });

      const bestMatchListingsWithImages = response.data?.data.map(l => {
        const listingImageId = l.relationships.images?.data[0]?.id?.uuid;

        if (!listingImageId) {
          return l;
        }

        const listingImage = imagesArray.find(i => i.id.uuid === listingImageId);
        if (listingImage) {
          l.images = [listingImage];
        }

        return l;
      });

      dispatch(queryBestMatchListingsSuccess(bestMatchListingsWithImages));
      return response;
    })
    .catch(e => dispatch(queryBestMatchListingsError(storableError(e))));
};

export const queryUpcomingSessions = (only, _config) => (dispatch, getState, sdk) => {
  dispatch(queryUpcomingSessionsRequest());

  // Include both accepted sessions and pending sessions (awaiting mentor approval)
  // For mentors (sale view), we also want to show sessions that need their attention
  const lastTransitions =
    only === 'sale'
      ? [
          'transition/accept',
          'transition/request-payment',
          'transition/request-payment-after-inquiry',
        ]
      : ['transition/accept'];

  const apiQueryParams = {
    only: only, //'sale',
    lastTransitions,
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page: 1,
    perPage: 100,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));

      if (response.data.data.length === 0) {
        return null;
      }

      const listings = response.data?.included?.filter(i => {
        return i.type === 'listing';
      });

      const bookings = response.data?.included?.filter(i => {
        return i.type === 'booking';
      });

      const users = response.data?.included?.filter(i => {
        return i.type === 'user';
      });

      const images = response.data?.included?.filter(i => {
        return i.type === 'image';
      });

      const upcomingSessionsWithDetails = response.data?.data?.map(t => {
        const listingId = t.relationships?.listing?.data?.id?.uuid;

        if (!listingId) {
          return t;
        }

        const listing = listings?.find(i => i.id.uuid === listingId);
        if (listing) {
          t.listing = listing;
        }

        const customerId = t.relationships?.customer?.data?.id?.uuid;

        if (!customerId) {
          return t;
        }

        const customer = users?.find(i => i.id.uuid === customerId);
        if (customer) {
          const customerProfileImageId = customer.relationships.profileImage?.data?.id?.uuid;
          if (customerProfileImageId) {
            customer.profileImage = images.find(i => i.id.uuid === customerProfileImageId);
          }

          t.customer = customer;
        }

        const providerId = t.relationships?.provider?.data?.id?.uuid;

        if (!providerId) {
          return t;
        }

        const provider = users?.find(i => i.id.uuid === providerId);
        if (provider) {
          const providerProfileImageId = provider.relationships.profileImage?.data?.id?.uuid;
          if (providerProfileImageId) {
            provider.profileImage = images?.find(i => i.id.uuid === providerProfileImageId);
          }

          t.provider = provider;
        }

        const bookingId = t.relationships?.booking?.data?.id?.uuid;

        if (!bookingId) {
          return t;
        }

        const booking = bookings?.find(i => i.id.uuid === bookingId);
        if (customer) {
          t.booking = booking;
        }

        return t;
      });

      dispatch(queryUpcomingSessionsSuccess(upcomingSessionsWithDetails));
      return response;
    })
    .catch(e => {
      dispatch(queryUpcomingSessionsError(storableError(e)));
      throw e;
    });
};

export const loadData = (_params, _search) => dispatch => {
  const pageAsset = { landingPage: `content/pages/${ASSET_NAME}.json` };
  return dispatch(fetchPageAssets(pageAsset, true));
};
